import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import withMobileDialog from '@material-ui/core/withMobileDialog';
import styled, { keyframes } from 'styled-components';
import { connect } from 'react-redux';
import { createForm, formShape } from 'rc-form';
import { colors } from '../../../constants/colors';
import { fontSize, fontFamily } from '../../../constants/text';
import { biggerThanOrEqualTo, smallerThanOrEqualTo } from '../../../core/util/styledComponents';
import TextField from '../../SolvPatternLibrary/TextField';
import Button, { ButtonVariations } from '../../SolvPatternLibrary/Button';
import Collapse from '@material-ui/core/Collapse';
import { getCurrentPath } from '../../../core/util/url';
import { apiPostDispatchable } from '../../../core/dapi';
import { getClinicLeadsUrl } from '../../../core/dapi/clinicLeads';
import { clinicLeadError, clinicLeadSuccess } from '../../../actions/clinicLead';

export const REPORT_INACCURATE_MODAL = 'reportInaccurateInfo';
const NAME_FIELD = 'name';
const EMAIL_FIELD = 'email';
const DETAILS_FIELD = 'details';
const COMPANY_FIELD = 'company';
const CLOSE_TIMEOUT = 3000;

const SmoothDialog = styled(Dialog)`
  -webkit-font-smoothing: antialiased;
  & .paper-root {
    overflow-y: unset;
  }
`;

const FormContainer = styled.div`
  margin: 0 auto;
  max-width: 600px;
  text-align: center;
  background-color: white;
  padding: 20px 40px;

  ${smallerThanOrEqualTo.md`
    padding: 20px;
  `}
`;

const Close = styled.div`
  width: 25px;
  height: 25px;
  position: relative;
  cursor: pointer;
  margin: 0 0 -10px auto;
  ${smallerThanOrEqualTo.md`
    margin 0 0 0 auto;
  `}

  &:before {
    content: '';
    left: 50%;
    height: 100%;
    width: 1px;
    position: absolute;
    transform: rotate(45deg);
    background-color: ${colors.squid};
  }

  &:after {
    content: '';
    left: 50%;
    height: 100%;
    width: 1px;
    position: absolute;
    transform: rotate(-45deg);
    background-color: ${colors.squid};
  }
`;

const Header = styled.div`
  font-size: ${fontSize.hellaLarge};
  font-family: ${fontFamily.bold};
  color: ${colors.squid};
  text-align: center;
  margin: 0 auto;

  ${smallerThanOrEqualTo.md`
    font-size: ${fontSize.extraLarge}
  `}
`;

const ActionContainer = styled.div`
  margin-top: 15px;
  width: 100%;
  display: flex;
  justify-content: flex-end;

  ${smallerThanOrEqualTo.md`
    flex-direction: column-reverse;
  `}
`;

const CancelButton = styled(Button)`
  color: ${colors.crunchBerry};
  border-color: ${colors.crunchBerry};

  ${smallerThanOrEqualTo.md`
    width: 100%;
  `}
`;

const SubmitButton = styled(CancelButton)`
  color: ${colors.white};
  margin: 0 0 15px;

  ${biggerThanOrEqualTo.md`
    margin: 0 0 0 15px;
  `}
`;

const ROOT_CLASS = 'details-root';
const INPUT_CLASS = 'input-root';
const DetailsField = styled(TextField)`
  & .${ROOT_CLASS} {
    height: 35vw;
    max-height: 200px;
  }

  & .${INPUT_CLASS} {
    height: 35vw !important;
    text-indent: 0;
    padding: 10px 22px;
    max-height: 200px;
  }
`;

const fill = keyframes`
  from {
    transform: scaleX(0);
  } to {
    transform: scaleX(1);
  }
`;

const ThanksForReport = styled.div`
  font-family: ${fontFamily.book};
  font-size: ${fontSize.large};
  color: ${colors.greenHope};
  max-width: 400px;
  text-align: center;
  position: relative;
  margin: 180px auto 220px;
  display: inline-block;

  &:after {
    content: '';
    animation: ${fill} ${CLOSE_TIMEOUT}ms linear 1;
    background-color: ${colors.greenHope};
    display: block;
    margin-top: 10px;
    height: 6px;
    width: 100%;
    transform-origin: left;
  }
`;

class ReportInaccurateInfoModal extends PureComponent {
  static propTypes = {
    submitInfo: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    form: formShape.isRequired,
    fullScreen: PropTypes.bool,
    submitSuccess: PropTypes.bool,
    open: PropTypes.bool,
  };

  state = { submitted: false };

  componentWillUnmount() {
    clearTimeout(this.closeTimeout);
  }

  onChange = ({ target: { id, value } }) => this.props.form.setFieldsValue({ [id]: value });

  getErrors = () => ({
    nameError: this.props.form.getFieldError(NAME_FIELD),
    emailError: this.props.form.getFieldError(EMAIL_FIELD),
    companyError: this.props.form.getFieldError(COMPANY_FIELD),
    detailsError: this.props.form.getFieldError(DETAILS_FIELD),
  });

  nameField = {
    ...this.props.form.getFieldProps(NAME_FIELD, {
      validateTrigger: 'onBlur',
      rules: [{ required: true, message: 'required' }],
    }),
    id: NAME_FIELD,
    label: 'Name',
    onChange: this.onChange,
  };

  emailField = {
    ...this.props.form.getFieldProps(EMAIL_FIELD, {
      validateTrigger: 'onBlur',
      rules: [
        { required: true, message: 'required' },
        { type: 'email', message: 'must be an email' },
      ],
    }),
    id: EMAIL_FIELD,
    type: 'email',
    label: 'Email',
    onChange: this.onChange,
  };

  companyField = {
    ...this.props.form.getFieldProps(COMPANY_FIELD),
    id: COMPANY_FIELD,
    label: 'Company',
    onChange: this.onChange,
  };

  detailsField = {
    ...this.props.form.getFieldProps(DETAILS_FIELD, {
      validateTrigger: 'onBlur',
      rules: [{ required: true, message: 'required' }],
    }),
    id: DETAILS_FIELD,
    label: 'Description of Inaccurate Info',
    onChange: this.onChange,
  };

  handleSubmit = e => {
    e.preventDefault();
    this.props.form.validateFields(this.validateAndSubmit);
  };

  validateAndSubmit = errors => {
    if (errors) return;
    const form = this.props.form.getFieldsValue();
    this.setState({ submitted: true });
    this.closeTimeout = setTimeout(this.props.onClose, CLOSE_TIMEOUT);
    this.props.submitInfo({
      NAME: form.name,
      EMAIL: form.email,
      COMPANY: form.company,
      DETAILS: form.details,
      SOURCE: 'cdp report info',
      SOURCE_URL: getCurrentPath(),
    });
  };

  helperNode = error => Array.isArray(error) && <span>{error[0]}</span>;

  render = () => {
    const { fullScreen, open, onClose, submitSuccess } = this.props;
    const { nameError, emailError, detailsError } = this.getErrors();

    return (
      <SmoothDialog
        open={open}
        maxWidth="md"
        onClose={onClose}
        fullScreen={fullScreen}
        PaperProps={{ classes: { root: 'paper-root' } }}
      >
        <FormContainer>
          <Close onClick={onClose} />
          <Header>Report Inaccurate Information</Header>
          <Collapse in={!this.state.submitted} timeout="auto">
            <form onSubmit={this.handleSubmit}>
              <TextField
                fullWidth
                error={Boolean(nameError)}
                helperText={this.helperNode(nameError)}
                {...this.nameField}
              />
              <TextField
                fullWidth
                error={Boolean(emailError)}
                helperText={this.helperNode(emailError)}
                {...this.emailField}
              />
              <TextField fullWidth {...this.companyField} />
              <DetailsField
                multiline
                fullWidth
                error={Boolean(detailsError)}
                helperText={this.helperNode(detailsError)}
                InputProps={{ classes: { root: ROOT_CLASS } }}
                classes={{ inputInput: INPUT_CLASS }}
                {...this.detailsField}
              />
              <ActionContainer>
                <CancelButton variation={ButtonVariations.tertiary} onClick={onClose}>
                  Cancel
                </CancelButton>
                <SubmitButton variation={ButtonVariations.pinkPrimary} type="submit">
                  Submit
                </SubmitButton>
              </ActionContainer>
            </form>
          </Collapse>
          <Collapse in={this.state.submitted} timeout="auto" mountOnEnter>
            <ThanksForReport>
              Thank you for your suggestion! <br /> We'll be in touch soon
            </ThanksForReport>
          </Collapse>
        </FormContainer>
      </SmoothDialog>
    );
  };
}

const mapStateToProps = state => ({
  submitSuccess: state.clinicLead.submitSuccess,
});

const mapDispatchToProps = dispatch => ({
  submitInfo: form =>
    dispatch(apiPostDispatchable(getClinicLeadsUrl(), form, clinicLeadSuccess, clinicLeadError)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withMobileDialog({ breakpoint: 'xs' })(createForm()(ReportInaccurateInfoModal)));
